import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';

import SensorHistory from './SensorHistory';
import {
  loadLastSensorsMetric,
  loadMachine,
  loadSensor,
  resetMachineState,
  subscribeToSensorsUpdateStream
} from '../../../../redux/machines/actions';
import {
  getSelectedMachine,
  getSelectedSensor,
  getSelectedSensorLastMetric
} from '../../../../redux/machines/selectors';
import MachineNotFound from '../../../../components/MachineNotFound/MachineNotFound';
import { isMachineNotFound } from '../../../../helpers/machineErrors';
import { getCommonLoadingState, getMetricsTimeFrame, isSuccess } from '../../../../helpers/utils';
import { getLoadingState } from '../../../../redux/rootSelectors';
import { METRICS_DEFAULT_TIME_FRAME_IN_HOURS } from '../../../../attrs/layout';
import { useRedirectIfInvalidId } from '../../../../hooks';

const ConnectedSensorHistory = () => {
  const dispatch = useDispatch();
  const { id, sensorType } = useParams();
  const { loadingState: machineLoadingState, data: machine } = useSelector(getSelectedMachine);
  const loadingState = useSelector(compose(getLoadingState, getSelectedSensor));
  const lastMetricLoadingState = useSelector(compose(getLoadingState, getSelectedSensorLastMetric));
  const { loadingState: wsLoadingState } = useSelector(state => state.stream);

  // Redirect if id is invalid
  useRedirectIfInvalidId(id);
  useRedirectIfInvalidId(sensorType);

  const loadNeededData = useCallback(() => {
    dispatch(loadMachine(id));
    dispatch(loadLastSensorsMetric(id, sensorType));
    dispatch(
      loadSensor(
        id,
        sensorType,
        getMetricsTimeFrame(METRICS_DEFAULT_TIME_FRAME_IN_HOURS),
        METRICS_DEFAULT_TIME_FRAME_IN_HOURS
      )
    );
  }, [dispatch, id, sensorType]);

  useEffect(() => {
    loadNeededData();
  }, [loadNeededData]);

  useEffect(() => {
    if (isSuccess(wsLoadingState.status)) {
      dispatch(subscribeToSensorsUpdateStream(id));
    }
  }, [dispatch, id, wsLoadingState]);

  useEffect(() => () => dispatch(resetMachineState()), [dispatch]);

  if (isMachineNotFound(machineLoadingState)) return <MachineNotFound />;

  return (
    <SensorHistory
      loadingState={getCommonLoadingState([
        ...(machine.id === id ? [] : [machineLoadingState]),
        loadingState,
        lastMetricLoadingState
      ])}
      reloadAction={loadNeededData}
    />
  );
};

export default ConnectedSensorHistory;
